/**
 * statusUtils.js
 *
 * Single source of truth for translating raw backend codes into the
 * human-readable labels StatusChip expects. Every choice field in the
 * Django models below is a numeric string ("0", "1", ...), so anything
 * that renders a status must go through here rather than re-guessing
 * the mapping in each component.
 *
 * Sources (confirmed from inventory/models.py, project/models.py, quiz app):
 * - MachineReservation.approved_status / BookWorkbench.approved_status
 * - Project.progress_status
 * - Machine.status1
 * - QuizAttempt.status + passed
 */

// inventory/models.py -> reservation_approval_choices / booking_approval_choices
export const RESERVATION_STATUS_MAP = {
    "0": "Pending",
    "1": "Approved",
    "2": "Rejected",
    "3": "Approved", // AutoApproved collapses to Approved for display
};

// project/models.py -> project_progress_status_choices
export const PROJECT_STATUS_MAP = {
    "1": "Not Started",
    "2": "In Progress",
    "3": "Completed",
};

// inventory/models.py -> status_choices (Machine.status1)
export const MACHINE_STATUS_MAP = {
    "0": "Available",
    "1": "Under Maintenance",
    "2": "Out of Order",
};

export function reservationStatusLabel(code) {
    return RESERVATION_STATUS_MAP[code] || "Pending";
}

export function projectStatusLabel(code) {
    return PROJECT_STATUS_MAP[code] || "Not Started";
}

export function machineStatusLabel(code) {
    return MACHINE_STATUS_MAP[code] || "Available";
}

/**
 * QuizAttempt has no single "status" that matches the mockup's
 * "Quiz Failed" chip — it's derived from status + passed.
 */
export function quizStatusLabel(attempt) {
    if (!attempt || attempt.attempt_status === "not started") return "Not Started";
    if (attempt.attempt_status === "in progress") return "In Progress";
    if (attempt.attempt_status === "completed") {
        return attempt.passed ? "Completed" : "Quiz Failed";
    }
    return "Not Started";
}

/**
 * Workshop has no stored status — derive Ongoing/Upcoming/Completed
 * from from_date/to_date (both plain dates, compared to today).
 */
export function workshopStatusLabel(fromDate, toDate) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const from = new Date(fromDate);
    const to = new Date(toDate);

    if (today < from) return "Upcoming";
    if (today > to) return "Completed";
    return "Ongoing";
}

export function formatDate(dateStr) {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleDateString("en-IN", {
        day: "numeric",
        month: "short",
        year: "numeric",
    });
}
